import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../state/AuthContext";

function FullPageLoader() {
  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--ink-3)",
        gap: "var(--space-2)",
      }}
    >
      <span className="spinner" aria-hidden="true" />
      Loading…
    </div>
  );
}

/**
 * Requires a signed-in, email-verified user (CLAUDE.md §7).
 * - Not signed in -> /login
 * - Signed in but unverified -> /verify-email
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, isVerified } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!isVerified) return <Navigate to="/verify-email" replace />;
  return <>{children}</>;
}

/** Admin-only route. Falls back to the workspace for non-admins. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, loading, isVerified, isAdmin } = useAuth();

  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isVerified) return <Navigate to="/verify-email" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** For auth pages: if already signed in + verified, send to the workspace. */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, loading, isVerified } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user && isVerified) return <Navigate to="/" replace />;
  return <>{children}</>;
}
